import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiService from '../services/apiService';
import socketService from '../services/socketService';
import webPushService from '../services/webPushService';
import { API } from '../config/apiConfig';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      apiService.setToken(savedToken);
      setToken(savedToken);
      apiService
        .getMe()
        .then((userData) => {
          setUser(userData);
          socketService.connect(savedToken);
          webPushService.init(savedToken);
        })
        .catch(() => {
          localStorage.removeItem('token');
          apiService.clearToken();
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const register = async (username, phone, password, displayName) => {
    setError(null);
    try {
      const data = await apiService.register(username, phone, password, displayName);
      apiService.setToken(data.token);
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem('token', data.token);
      socketService.connect(data.token);
      webPushService.init(data.token);
      return true;
    } catch (err) {
      const msg = err.response?.data?.error || 'Registration failed';
      setError(msg);
      return false;
    }
  };

  const login = async (phone, password, rememberMe = false) => {
    setError(null);
    try {
      const data = await apiService.login(phone, password);
      apiService.setToken(data.token);
      setToken(data.token);
      setUser(data.user);
      if (rememberMe) {
        localStorage.setItem('token', data.token);
      } else {
        localStorage.removeItem('token');
      }
      socketService.connect(data.token);
      webPushService.init(data.token);
      return true;
    } catch (err) {
      const msg = err.response?.data?.error || 'Login failed';
      setError(msg);
      return false;
    }
  };

  const logout = () => {
    socketService.disconnect();
    setUser(null);
    setToken(null);
    apiService.clearToken();
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider
      value={{ user, token, loading, error, isLoggedIn: !!user, register, login, logout, clearError }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
